import { isEqual } from 'lodash';
import { useReducer } from 'react';

import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';

export type BulkSelectState<T> =
  | {
      state: 'bulk';
      // The set of excluded items
      toggledItems: T[];
    }
  | {
      state: 'individual';
      // The set of included items
      toggledItems: T[];
    };

export type BulkSelectAction<T> =
  | { type: 'toggle'; item: T }
  | { type: 'toggle all' }
  | { type: 'reset' }
  | { type: 'force toggle all' }
  | { type: 'force toggle many'; items: T[] };

function bulkSelectReducer<T>(
  currentState: BulkSelectState<T>,
  action: BulkSelectAction<T>,
): BulkSelectState<T> {
  switch (action.type) {
    case 'toggle': {
      const toggledItems = [...currentState.toggledItems];
      const itemIndex = toggledItems.findIndex((item) =>
        isEqual(item, action.item),
      );
      if (itemIndex === -1) {
        toggledItems.push(action.item);
      } else {
        toggledItems.splice(itemIndex, 1);
      }
      return { ...currentState, toggledItems };
    }
    case 'toggle all': {
      if (currentState.toggledItems.length > 0) {
        return { ...currentState, toggledItems: [] };
      } else if (currentState.state === 'bulk') {
        return { state: 'individual', toggledItems: [] };
      } else {
        return { state: 'bulk', toggledItems: [] };
      }
    }
    case 'reset': {
      return { state: 'individual', toggledItems: [] };
    }
    case 'force toggle all': {
      return { state: 'bulk', toggledItems: [] };
    }
    case 'force toggle many': {
      return { state: 'individual', toggledItems: action.items };
    }
    default:
      throw assertUnreachableOrReturnDefault(action, currentState);
  }
}

export const useBulkSelect = <T>() => {
  const [bulkSelectState, dispatchBulkSelectAction] = useReducer(
    bulkSelectReducer<T>,
    { state: 'individual', toggledItems: [] },
  );
  return {
    bulkSelectState,
    dispatchBulkSelectAction,
  };
};
